import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { 
  CreditCard, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  RefreshCw, 
  Eye, 
  Settings,
  Crown,
  Zap,
  Shield,
  Users,
  DollarSign,
  Activity
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSchool } from '../../contexts/SchoolContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { SUBSCRIPTION_PLANS, ADD_ONS } from '../../types/subscription';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import AppLayout from '../layout/AppLayout';
import toast from 'react-hot-toast';
import { getStripePublishableKey } from '../../config/stripe';

interface TestCard {
  number: string;
  description: string;
  expectedResult: string;
  type: 'success' | 'decline' | 'error';
}

const TEST_CARDS: TestCard[] = [
  {
    number: '4242424242424242',
    description: 'Visa - Success',
    expectedResult: 'Payment succeeds',
    type: 'success'
  },
  {
    number: '4000000000000002',
    description: 'Visa - Declined',
    expectedResult: 'Payment declined',
    type: 'decline'
  },
  {
    number: '4000000000009995',
    description: 'Visa - Insufficient Funds',
    expectedResult: 'Payment fails - insufficient funds',
    type: 'decline'
  },
  {
    number: '4000000000000069',
    description: 'Visa - CVC Check Fails',
    expectedResult: 'Payment fails - CVC check',
    type: 'decline'
  },
  {
    number: '4000000000000127',
    description: 'Visa - Postal Code Fails',
    expectedResult: 'Payment fails - postal code',
    type: 'decline'
  },
  {
    number: '4000000000000119',
    description: 'Visa - Processing Error',
    expectedResult: 'Processing error',
    type: 'error'
  }
];

export default function StripeTestPage() {
  useDocumentTitle('Stripe Testing');
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { school } = useSchool();
  const { subscription, currentPlan, loading: subscriptionLoading } = useSubscription();
  
  const [stripeStatus, setStripeStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [logs, setLogs] = useState<string[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);
  const [testMode, setTestMode] = useState<'live' | 'simulate'>('live');

  useEffect(() => {
    initializeStripe();
  }, []);

  const initializeStripe = async () => {
    try {
      addLog('Initializing Stripe...');
      const stripe = await loadStripe(getStripePublishableKey());
      
      if (stripe) {
        setStripeStatus('ready');
        addLog('✅ Stripe initialized successfully');
        addLog(`🔑 Using publishable key: ${getStripePublishableKey()?.slice(0, 20)}...`);
      } else {
        throw new Error('Failed to initialize Stripe');
      }
    } catch (error) {
      setStripeStatus('error');
      addLog(`❌ Stripe initialization failed: ${error}`);
    }
  };

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const handleTestCheckout = async (planId: string, testCard?: string) => {
    if (!user || !school) {
      toast.error('Please ensure you are logged in with a school');
      return;
    }

    try {
      setProcessing(true);
      addLog(`🚀 Starting checkout for plan: ${planId}`);
      
      const stripe = await loadStripe(getStripePublishableKey());
      if (!stripe) {
        throw new Error('Stripe not initialized');
      }

      const plan = SUBSCRIPTION_PLANS.find(p => p.id === planId);
      if (!plan || plan.id === 'free') {
        throw new Error('Invalid plan selected');
      }

      addLog(`💰 Plan: ${plan.name} - $${plan.price}/month`);
      addLog(`🏫 School: ${school.name} (${school.id})`);
      
      if (testCard) {
        addLog(`💳 Using test card: ${testCard}`);
      }

      // Create line items
      const lineItems = [
        {
          price_data: {
            currency: 'usd',
            product_data: {
              name: `${plan.name} Plan`,
              description: `CampusConnect ${plan.name} subscription for ${school.name}`,
            },
            unit_amount: plan.price * 100,
            recurring: {
              interval: 'month',
            },
          },
          quantity: plan.minUsers || 1,
        },
      ];

      // Add selected add-ons
      selectedAddOns.forEach(addOnId => {
        const addOn = ADD_ONS.find(a => a.id === addOnId);
        if (addOn) {
          lineItems.push({
            price_data: {
              currency: 'usd',
              product_data: {
                name: addOn.name,
                description: addOn.description,
              },
              unit_amount: addOn.price * 100,
              recurring: {
                interval: 'month',
              },
            },
            quantity: 1,
          });
          addLog(`➕ Add-on: ${addOn.name} - $${addOn.price}/month`);
        }
      });

      const successUrl = `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}&school_id=${school.id}&plan_id=${planId}&test=true`;
      const cancelUrl = `${window.location.origin}/stripe-test?cancelled=true`;

      addLog('🔄 Redirecting to Stripe Checkout...');

      const { error } = await stripe.redirectToCheckout({
        mode: 'subscription',
        line_items: lineItems,
        success_url: successUrl,
        cancel_url: cancelUrl,
        customer_email: user.email || undefined,
        client_reference_id: school.id,
        metadata: {
          school_id: school.id,
          user_id: user.uid,
          plan_id: planId,
          add_ons: selectedAddOns.join(','),
          test_mode: 'true',
          test_card: testCard || 'none'
        },
      });

      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      addLog(`❌ Checkout failed: ${error}`);
      toast.error(`Checkout failed: ${error}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleSimulateWebhook = (eventType: string) => {
    addLog(`🎯 Simulating webhook: ${eventType}`);
    
    const mockData = {
      'payment_succeeded': {
        id: 'in_test_' + Date.now(),
        amount_paid: selectedPlan === 'standard' ? 200 : 400,
        currency: 'usd',
        subscription: 'sub_test_' + Date.now(),
        customer: 'cus_test_' + Date.now()
      },
      'payment_failed': {
        id: 'in_test_' + Date.now(),
        amount_due: selectedPlan === 'standard' ? 200 : 400,
        currency: 'usd',
        subscription: 'sub_test_' + Date.now(),
        customer: 'cus_test_' + Date.now()
      },
      'subscription_updated': {
        id: 'sub_test_' + Date.now(),
        status: 'active',
        current_period_start: Math.floor(Date.now() / 1000),
        current_period_end: Math.floor((Date.now() + 30 * 24 * 60 * 60 * 1000) / 1000),
        cancel_at_period_end: false
      }
    };

    addLog(`📦 Mock data: ${JSON.stringify(mockData[eventType as keyof typeof mockData], null, 2)}`);
    toast.info(`Webhook ${eventType} simulated - check logs for details`);
  };

  const toggleAddOn = (addOnId: string) => {
    setSelectedAddOns(prev => 
      prev.includes(addOnId) 
        ? prev.filter(id => id !== addOnId)
        : [...prev, addOnId]
    );
  };

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'free': return <Users className="w-6 h-6" />;
      case 'standard': return <Zap className="w-6 h-6" />;
      case 'premium': return <Crown className="w-6 h-6" />;
      default: return <Shield className="w-6 h-6" />;
    }
  };

  const getStatusIndicator = () => {
    switch (stripeStatus) {
      case 'ready':
        return (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="w-5 h-5" />
            <span>Stripe Ready</span>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center gap-2 text-red-600">
            <XCircle className="w-5 h-5" />
            <span>Stripe Error</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-2 text-yellow-600">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading...</span>
          </div>
        );
    }
  };

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-primary-500" />
              <div>
                <h1 className="text-3xl font-bold text-text-primary">Stripe Testing Dashboard</h1>
                <p className="text-text-secondary">Test payment flows and subscription management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {getStatusIndicator()}
              <button
                onClick={() => navigate('/billing')}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Go to Live Billing
              </button>
            </div>
          </div>

          {/* Current Status */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-surface-light p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-primary-500" />
                <span className="font-medium">Current Plan</span>
              </div>
              <p className="text-xl font-bold">{currentPlan?.name || 'Free'}</p>
              <p className="text-sm text-text-secondary">${currentPlan?.price || 0}/month</p>
            </div>

            <div className="bg-surface-light p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-5 h-5 text-green-500" />
                <span className="font-medium">Status</span>
              </div>
              <p className="text-xl font-bold">{subscription?.status || 'Free'}</p>
              <p className="text-sm text-text-secondary">
                {subscription ? 'Subscribed' : 'No subscription'}
              </p>
            </div>

            <div className="bg-surface-light p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-blue-500" />
                <span className="font-medium">School</span>
              </div>
              <p className="text-xl font-bold">{school?.name || 'No School'}</p>
              <p className="text-sm text-text-secondary">{school?.code || 'No code'}</p>
            </div>

            <div className="bg-surface-light p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="w-5 h-5 text-gray-500" />
                <span className="font-medium">Test Mode</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setTestMode('live')}
                  className={`px-3 py-1 rounded text-sm ${testMode === 'live' ? 'bg-primary-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  Live
                </button>
                <button
                  onClick={() => setTestMode('simulate')}
                  className={`px-3 py-1 rounded text-sm ${testMode === 'simulate' ? 'bg-primary-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  Simulate
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Plans & Testing */}
          <div className="space-y-6">
            {/* Subscription Plans */}
            <div className="bg-surface-light p-6 rounded-lg">
              <h2 className="text-xl font-semibold text-text-primary mb-4">Test Subscription Plans</h2>
              
              <div className="space-y-4">
                {SUBSCRIPTION_PLANS.filter(plan => plan.id !== 'free').map((plan) => (
                  <div
                    key={plan.id}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      selectedPlan === plan.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getPlanIcon(plan.id)}
                        <div>
                          <h3 className="font-semibold text-text-primary">{plan.name}</h3>
                          <p className="text-text-secondary">${plan.price}/month</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {plan.isPopular && (
                          <span className="px-2 py-1 bg-primary-500 text-white text-xs rounded-full">
                            Popular
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTestCheckout(plan.id);
                          }}
                          disabled={processing || stripeStatus !== 'ready' || !school}
                          className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 disabled:opacity-50 transition-colors"
                        >
                          {processing ? 'Processing...' : 'Test Checkout'}
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <p className="text-sm text-text-secondary mb-2">Features:</p>
                      <ul className="text-xs text-text-secondary space-y-1">
                        {plan.features.slice(0, 3).map((feature, index) => (
                          <li key={index}>• {feature}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Add-ons */}
            <div className="bg-surface-light p-6 rounded-lg">
              <h2 className="text-xl font-semibold text-text-primary mb-4">Test Add-ons</h2>
              
              <div className="space-y-3">
                {ADD_ONS.map((addOn) => (
                  <div
                    key={addOn.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedAddOns.includes(addOn.id)
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => toggleAddOn(addOn.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-text-primary">{addOn.name}</h4>
                        <p className="text-sm text-text-secondary">{addOn.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">${addOn.price}/month</p>
                        {selectedAddOns.includes(addOn.id) && (
                          <CheckCircle className="w-5 h-5 text-primary-500 mt-1" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Test Cards */}
            <div className="bg-surface-light p-6 rounded-lg">
              <h2 className="text-xl font-semibold text-text-primary mb-4">Test Card Numbers</h2>
              
              <div className="space-y-2">
                {TEST_CARDS.map((card, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-background rounded">
                    <div className="flex-1">
                      <div className="font-mono text-sm">{card.number}</div>
                      <div className="text-sm text-text-secondary">{card.description}</div>
                    </div>
                    <div className="flex-1 text-sm text-text-secondary">
                      {card.expectedResult}
                    </div>
                    <button
                      onClick={() => selectedPlan && handleTestCheckout(selectedPlan, card.number)}
                      disabled={!selectedPlan || processing}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        card.type === 'success' 
                          ? 'bg-green-500 text-white hover:bg-green-600' 
                          : card.type === 'decline'
                          ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                          : 'bg-red-500 text-white hover:bg-red-600'
                      } disabled:opacity-50`}
                    >
                      Test
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Webhook Simulation */}
            <div className="bg-surface-light p-6 rounded-lg">
              <h2 className="text-xl font-semibold text-text-primary mb-4">Webhook Simulation</h2>
              
              <div className="space-y-2">
                {['payment_succeeded', 'payment_failed', 'subscription_updated', 'subscription_cancelled'].map((event) => (
                  <button
                    key={event}
                    onClick={() => handleSimulateWebhook(event)}
                    className="w-full p-2 text-left bg-background hover:bg-gray-50 rounded transition-colors"
                  >
                    <div className="font-medium">{event.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                    <div className="text-sm text-text-secondary">Simulate {event} webhook event</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Logs & Debug */}
          <div className="space-y-6">
            {/* Environment Status */}
            <div className="bg-surface-light p-6 rounded-lg">
              <h2 className="text-xl font-semibold text-text-primary mb-4">Environment Status</h2>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span>Stripe Publishable Key</span>
                  <span className={`px-2 py-1 rounded text-sm ${getStripePublishableKey() ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {getStripePublishableKey() ? 'Set' : 'Missing'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span>Firebase Project</span>
                  <span className={`px-2 py-1 rounded text-sm ${import.meta.env.VITE_FIREBASE_PROJECT_ID ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {import.meta.env.VITE_FIREBASE_PROJECT_ID || 'Missing'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span>User Authenticated</span>
                  <span className={`px-2 py-1 rounded text-sm ${user ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {user ? 'Yes' : 'No'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span>School Selected</span>
                  <span className={`px-2 py-1 rounded text-sm ${school ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {school ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>

              {(!user || !school) && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <div className="flex items-center gap-2 text-yellow-800">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">Setup Required</span>
                  </div>
                  <p className="text-sm text-yellow-700 mt-1">
                    {!user && 'Please log in first. '}
                    {!school && 'Please select or create a school. '}
                  </p>
                  <div className="mt-2">
                    {!user && (
                      <button
                        onClick={() => navigate('/login')}
                        className="mr-2 px-3 py-1 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600"
                      >
                        Log In
                      </button>
                    )}
                    {user && !school && (
                      <button
                        onClick={() => navigate('/dashboard')}
                        className="px-3 py-1 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600"
                      >
                        Select School
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Activity Logs */}
            <div className="bg-surface-light p-6 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-text-primary">Activity Logs</h2>
                <button
                  onClick={clearLogs}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  Clear
                </button>
              </div>
              
              <div className="bg-gray-900 text-green-400 p-4 rounded font-mono text-sm max-h-96 overflow-y-auto">
                {logs.length === 0 ? (
                  <div className="text-gray-500">No logs yet...</div>
                ) : (
                  logs.map((log, index) => (
                    <div key={index} className="mb-1">
                      {log}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-surface-light p-6 rounded-lg">
              <h2 className="text-xl font-semibold text-text-primary mb-4">Quick Actions</h2>
              
              <div className="space-y-2">
                <button
                  onClick={() => window.open('https://dashboard.stripe.com/test/payments', '_blank')}
                  className="w-full p-2 text-left bg-background hover:bg-gray-50 rounded transition-colors"
                >
                  <div className="font-medium">View Stripe Dashboard</div>
                  <div className="text-sm text-text-secondary">Open Stripe test dashboard</div>
                </button>
                
                <button
                  onClick={() => navigate('/billing')}
                  className="w-full p-2 text-left bg-background hover:bg-gray-50 rounded transition-colors"
                >
                  <div className="font-medium">Live Billing Page</div>
                  <div className="text-sm text-text-secondary">Go to production billing</div>
                </button>
                
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full p-2 text-left bg-background hover:bg-gray-50 rounded transition-colors"
                >
                  <div className="font-medium">Dashboard</div>
                  <div className="text-sm text-text-secondary">Return to main dashboard</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}